import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useVipDataQuery } from '@/hooks/queries/useVipQuery';
import type { VipTier, VipStatus, VipTierUpgrade, PreviousBonusInfo } from '@/hooks/queries/useVipQuery';

// Re-export types for consumers that import from VipContext
export type { VipTier, VipStatus, VipTierUpgrade, PreviousBonusInfo };

interface VipContextType {
  vipTiers: VipTier[];
  userVipStatus: VipStatus | null;
  isLoading: boolean;
  refreshVipData: () => Promise<void>;
  tierUpgrade: VipTierUpgrade | null;
  clearTierUpgrade: () => void;
}

const VipContext = createContext<VipContextType | undefined>(undefined);

export const useVip = () => {
  const context = useContext(VipContext);
  if (!context) {
    // Return a safe default instead of throwing to prevent app crashes
    return {
      vipTiers: [],
      userVipStatus: null,
      isLoading: false,
      refreshVipData: async () => {},
      tierUpgrade: null,
      clearTierUpgrade: () => {},
    };
  }
  return context;
};

export const VipProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const authContext = useAuth();
  const user = authContext?.user || null;
  
  // Use unified VIP data query (1 API call instead of 2)
  const { data: vipData, isLoading, refetch: refetchVipData } = useVipDataQuery();
  
  // Extract data from unified response
  const vipTiers = vipData?.tiers || [];
  const userVipStatus = vipData?.vipStatus || null;
  const apiTierUpgrade = vipData?.tierUpgrade || null;
  
  // Local state for tier upgrade modal
  const [tierUpgrade, setTierUpgrade] = useState<VipTierUpgrade | null>(null);
  
  // Refs for tier upgrade detection
  const previousTierIdRef = useRef<number | string | null>(null);
  const lastShownTierIdRef = useRef<number | string | null>(null);

  // Process tier upgrades when status changes
  useEffect(() => {
    if (!userVipStatus || vipTiers.length === 0) {
      return;
    }

    const newTierId = userVipStatus.current_tier_id;
    const oldTierId = previousTierIdRef.current;
    const lastShownTierId = lastShownTierIdRef.current;

    // Check if API returned tier upgrade info (most reliable source)
    if (apiTierUpgrade && apiTierUpgrade.oldTier && apiTierUpgrade.newTier) {
      const normalizedNewTierId = String(apiTierUpgrade.newTier.id);
      
      // Only show if we haven't shown this tier upgrade modal yet
      if (lastShownTierId !== normalizedNewTierId) {
        setTimeout(() => {
          try {
            const upgradeData: VipTierUpgrade = {
              oldTier: {
                id: String(apiTierUpgrade.oldTier.id),
                tier_name: String(apiTierUpgrade.oldTier.tier_name),
                tier_level: Number(apiTierUpgrade.oldTier.tier_level) || 0,
                tier_color: String(apiTierUpgrade.oldTier.tier_color || '#666'),
              },
              newTier: {
                id: String(apiTierUpgrade.newTier.id),
                tier_name: String(apiTierUpgrade.newTier.tier_name),
                tier_level: Number(apiTierUpgrade.newTier.tier_level) || 0,
                tier_color: String(apiTierUpgrade.newTier.tier_color || '#FFD700'),
                tier_reach_reward_gc: Number(apiTierUpgrade.newTier.tier_reach_reward_gc || 0),
                tier_reach_reward_sc: Number(apiTierUpgrade.newTier.tier_reach_reward_sc || 0),
              },
              bonusAwarded: apiTierUpgrade.bonusAwarded,
              previousBonusInfo: apiTierUpgrade.previousBonusInfo || null,
            };
            
            setTierUpgrade(upgradeData);
            lastShownTierIdRef.current = normalizedNewTierId;
          } catch {
            // Silently fail - tier upgrade display is not critical
          }
        }, 100);
      }
    }
    // Fallback: detect tier change locally if API didn't provide upgrade info
    else {
      const tierChanged = oldTierId !== null && oldTierId !== newTierId && newTierId;
      const shouldShowUpgrade = lastShownTierId !== String(newTierId) && newTierId;

      if (tierChanged && shouldShowUpgrade) {
        try {
          const normalizedOldTierId = String(oldTierId);
          const normalizedNewTierId = String(newTierId);

          const oldTier = vipTiers.find(t => String(t.id) === normalizedOldTierId);
          const newTier = vipTiers.find(t => String(t.id) === normalizedNewTierId);

          if (oldTier && newTier) {
            const oldTierValid = oldTier.id !== null && oldTier.tier_name && oldTier.tier_level !== null;
            const newTierValid = newTier.id !== null && newTier.tier_name && newTier.tier_level !== null;

            // Only proceed if it's an actual upgrade (new tier level is higher)
            if (oldTierValid && newTierValid && newTier.tier_level > oldTier.tier_level) {
              setTimeout(() => {
                try {
                  const rewardGcNum = Number(newTier.tier_reach_reward_gc || 0);
                  const rewardScNum = Number(newTier.tier_reach_reward_sc || 0);

                  const upgradeData: VipTierUpgrade = {
                    oldTier: {
                      id: String(oldTier.id),
                      tier_name: String(oldTier.tier_name),
                      tier_level: Number(oldTier.tier_level) || 0,
                      tier_color: String(oldTier.tier_color || '#666'),
                    },
                    newTier: {
                      id: String(newTier.id),
                      tier_name: String(newTier.tier_name),
                      tier_level: Number(newTier.tier_level) || 0,
                      tier_color: String(newTier.tier_color || '#FFD700'),
                      tier_reach_reward_gc: rewardGcNum,
                      tier_reach_reward_sc: rewardScNum,
                    },
                    // Don't assume bonus was awarded - only the API can confirm this
                    bonusAwarded: false,
                    // Mark as manual upgrade since API didn't return tierUpgrade
                    isManualUpgrade: true,
                  };

                  setTierUpgrade(upgradeData);
                  lastShownTierIdRef.current = normalizedNewTierId;
                } catch {
                  // Silently fail - tier upgrade display is not critical
                }
              }, 100);
            }
          }
        } catch {
          // Silently fail - tier upgrade processing is not critical
        }
      } else if (lastShownTierId && lastShownTierId !== String(newTierId) && newTierId) {
        // Check if current tier is higher than the last tier we showed a modal for
        const currentTier = vipTiers.find(t => String(t.id) === String(newTierId));
        const lastShownTier = vipTiers.find(t => String(t.id) === String(lastShownTierId));

        if (currentTier && lastShownTier && currentTier.tier_level > lastShownTier.tier_level) {
          const upgradeData: VipTierUpgrade = {
            oldTier: {
              id: String(lastShownTier.id),
              tier_name: String(lastShownTier.tier_name),
              tier_level: Number(lastShownTier.tier_level) || 0,
              tier_color: String(lastShownTier.tier_color || '#666'),
            },
            newTier: {
              id: String(currentTier.id),
              tier_name: String(currentTier.tier_name),
              tier_level: Number(currentTier.tier_level) || 0,
              tier_color: String(currentTier.tier_color || '#FFD700'),
              tier_reach_reward_gc: Number(currentTier.tier_reach_reward_gc || 0),
              tier_reach_reward_sc: Number(currentTier.tier_reach_reward_sc || 0),
            },
            // Don't assume bonus was awarded - only the API can confirm this
            bonusAwarded: false,
            // Mark as manual upgrade since API didn't return tierUpgrade
            isManualUpgrade: true,
          };

          setTimeout(() => {
            setTierUpgrade(upgradeData);
            lastShownTierIdRef.current = String(newTierId);
          }, 100);
        }
      }
    }

    // Update previous tier ID for next comparison
    previousTierIdRef.current = newTierId;

    // Initialize lastShownTierId if this is the first load
    if (lastShownTierIdRef.current === null && newTierId) {
      lastShownTierIdRef.current = String(newTierId);
    }
  }, [userVipStatus, apiTierUpgrade, vipTiers]);

  // Reset refs when user logs out
  useEffect(() => {
    if (!user) {
      previousTierIdRef.current = null;
      lastShownTierIdRef.current = null;
      setTierUpgrade(null);
    }
  }, [user]);

  const clearTierUpgrade = useCallback(() => {
    setTierUpgrade(null);
  }, []);

  const refreshVipData = useCallback(async () => {
    try {
      await refetchVipData();
    } catch {
      // Silently fail - VIP data refresh is not critical
    }
  }, [refetchVipData]);

  const contextValue = {
    vipTiers: vipTiers || [],
    userVipStatus: userVipStatus || null,
    isLoading,
    refreshVipData,
    tierUpgrade: tierUpgrade || null,
    clearTierUpgrade,
  };

  return (
    <VipContext.Provider value={contextValue}>
      {children}
    </VipContext.Provider>
  );
};
